import type { ReactNode } from "react";
import type { FormatSection, Resume, ResumeFormat } from "@/lib/api/resumeService";
import { resumeDate, roleSpan } from "@/lib/posting";

/** The type and the spacing a sheet sets a resume at, as class names. */
export interface SheetType {
    name: string;
    headline: string;
    contact: string;
    heading: string;
    body: string;
    /** A role's title, or a degree. */
    title: string;
    /** A role's company and dates, or a school and its year. */
    detail: string;
    leading: string;
    /** Between a heading and what is under it, and between entries. */
    gap: string;
    /** Between one bullet and the next. */
    bulletGap: string;
    /** In from the edge to a bullet's text. */
    bulletIndent: string;
}

/**
 * A resume as the PDF sets it, inside whatever sheet holds it. The name, the headline and the
 * contact line are centered. Below them everything is one column from the left edge, in the
 * format's order: each section under a bold heading in capitals with a rule under it; a role as
 * its title, its company and place, its dates, then its bullets; a school as its degree, the
 * school, then the year. A section with nothing in it is left out wherever it comes.
 */
export default function SheetLayout({
    resume,
    format,
    name,
    type,
}: {
    resume: Resume;
    format: ResumeFormat;
    /** The name as shown, which may stand in for one not yet written. */
    name: string;
    type: SheetType;
}) {
    const contact = [resume.location, resume.email, resume.phone, ...resume.links]
        .filter(Boolean)
        .join(" | ");
    const certifications = resume.certifications ?? [];

    const heading = (text: string) => (
        <span className={`border-b border-ink pb-px font-bold uppercase ${type.heading}`}>
            {text}
        </span>
    );
    const block = (key: FormatSection, children: ReactNode) => (
        <div key={key} className={`flex flex-col ${type.gap}`}>
            {children}
        </div>
    );
    const bullets = (items: string[]) => (
        <ul
            className={`m-0 flex list-disc flex-col ${type.bulletIndent} ${type.body} ${type.leading} ${type.bulletGap}`}
        >
            {items.map((item, place) => (
                <li key={place}>{item}</li>
            ))}
        </ul>
    );

    const section = (key: FormatSection): ReactNode => {
        switch (key) {
            case "SUMMARY":
                return resume.summary
                    ? block(
                          key,
                          <>
                              {heading("Summary")}
                              <span className={`${type.body} ${type.leading}`}>
                                  {resume.summary}
                              </span>
                          </>,
                      )
                    : null;
            case "SKILLS":
                return resume.skills.length > 0
                    ? block(
                          key,
                          <>
                              {heading("Skills")}
                              <span className={`${type.body} ${type.leading}`}>
                                  {resume.skills.join(", ")}
                              </span>
                          </>,
                      )
                    : null;
            case "EXPERIENCE":
                return resume.experience.length > 0
                    ? block(
                          key,
                          <>
                              {heading("Experience")}
                              {resume.experience.map((role, index) => {
                                  const span = roleSpan(role, format.dates);
                                  return (
                                      <div key={index} className="flex flex-col">
                                          <span className={`font-bold ${type.title}`}>
                                              {role.title}
                                          </span>
                                          <span className={`font-bold ${type.detail}`}>
                                              {[role.company, role.location]
                                                  .filter(Boolean)
                                                  .join(", ")}
                                          </span>
                                          {span && (
                                              <span className={`font-bold ${type.detail}`}>
                                                  {span}
                                              </span>
                                          )}
                                          {role.bullets.length > 0 && bullets(role.bullets)}
                                      </div>
                                  );
                              })}
                          </>,
                      )
                    : null;
            case "EDUCATION":
                return resume.education.length > 0
                    ? block(
                          key,
                          <>
                              {heading("Education")}
                              {resume.education.map((entry, index) => (
                                  <div key={index} className="flex flex-col">
                                      {entry.degree && (
                                          <span className={`font-bold ${type.title}`}>
                                              {entry.degree}
                                          </span>
                                      )}
                                      <span
                                          className={`font-bold ${entry.degree ? type.detail : type.title}`}
                                      >
                                          {entry.school}
                                      </span>
                                      {entry.year && (
                                          <span className={`font-bold ${type.detail}`}>
                                              {resumeDate(entry.year, format.dates)}
                                          </span>
                                      )}
                                  </div>
                              ))}
                          </>,
                      )
                    : null;
            case "CERTIFICATIONS":
                return certifications.length > 0
                    ? block(
                          key,
                          <>
                              {heading("Certifications")}
                              {bullets(
                                  certifications.map((entry) =>
                                      [
                                          entry.name,
                                          entry.issuer,
                                          entry.year && resumeDate(entry.year, format.dates),
                                      ]
                                          .filter(Boolean)
                                          .join(", "),
                                  ),
                              )}
                          </>,
                      )
                    : null;
        }
    };

    return (
        <>
            <div className="flex flex-col items-center gap-0.5 text-center">
                <span className={`font-bold uppercase leading-tight ${type.name}`}>{name}</span>
                {resume.headline && (
                    <span className={`font-bold ${type.headline}`}>{resume.headline}</span>
                )}
                {contact && <span className={type.contact}>{contact}</span>}
            </div>
            {format.sections.map(section)}
        </>
    );
}
